"use client";

import type { Definition } from "@asosteam/asos-web-datalayer";
import type { PushToDataLayer } from "@asosteam/asos-web-event-tracking-bridge";
import Script from "next/script";
import {
  useEffect,
  useContext,
  createContext,
  useState,
  useCallback,
} from "react";
import { usePromise } from "@repo/ui/use-promise";
import { getApiUrl } from "@repo/utils/get-api-url";
import { handleOneTrustLoad } from "@/components/consent";
import {
  ADOBE_ANALYTICS_SCRIPT_URL,
  MIXPANEL_RECORD_SESSIONS_PERCENT,
} from "@/constants";
import { createWebVitalsFilter } from "./create-web-vitals-filter";

// The data layer measures document vitals on every pageData event.
// Keep separate sink filters for the document lifetime, including provider remounts.
let initialPathname: string | undefined;
let hasNavigated = false;
const shouldSendGtmEvent = createWebVitalsFilter(() => hasNavigated);
const shouldSendMixpanelEvent = createWebVitalsFilter(() => hasNavigated);

type AnalyticsContext = {
  pushToDataLayer: PushToDataLayer;
  resetDataLayer: (pathname?: string | null) => Promise<void>;
  setSuperProperties: (properties: Record<string, unknown>) => Promise<void>;
};

const defaultAnalyticsContextValue = {
  pushToDataLayer: async () => null,
  resetDataLayer: async () => {},
  setSuperProperties: async () => {},
} as AnalyticsContext;

export const AnalyticsContext = createContext(defaultAnalyticsContextValue);
export const useAnalytics = () => useContext(AnalyticsContext);

const logger = (error: Error) => {
  if (console) {
    console.error(error);
  }
};

const createWaitingSink = () => {
  let resolveWaitingSink: () => void = () => {};
  const waitingSink = new Promise<void>((resolve) => {
    resolveWaitingSink = resolve;
  });

  return { waitingSink, resolveWaitingSink };
};

const {
  waitingSink: firstPageDataSent,
  resolveWaitingSink: resolveFirstPageDataSent,
} = createWaitingSink();

// create fake source for now just to kick off the bridging (will refactor when tracking bridge is updated)
const createFakeSource = () => {
  const { port1: publicEventTarget } = new MessageChannel();
  return Promise.resolve(publicEventTarget);
};

export const AnalyticsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const fakeSource = createFakeSource();
  const { waitingSink, resolveWaitingSink } = createWaitingSink();
  const { promise, resolve } = usePromise<AnalyticsContext>();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (isReady) {
      return;
    }

    import("@asosteam/asos-web-event-tracking-bridge").then(
      ({
        initialise,
        createGtmSink,
        createMixpanelSink,
        setSuperProperties: bridgeSetSuperProperties,
      }) => {
        const gtmSink = createGtmSink(
          "GTM-WSDVJP8M",
          logger,
          true,
          undefined,
          "GTM-PQSSZK68",
        );

        const MIXPANEL_PROJECT_TOKEN = "topshop";
        const targetAnalyticsApiEndpoint = getApiUrl({
          path: "/customer/signals/v1",
          searchParams: {},
        });

        const mixpanelSink = createMixpanelSink(MIXPANEL_PROJECT_TOKEN, {
          api_host: targetAnalyticsApiEndpoint,
          record_sessions_percent: MIXPANEL_RECORD_SESSIONS_PERCENT,
          record_heatmap_data: true,
          autocapture: {
            pageview: null,
            click: true,
            dead_click: true,
            rage_click: true,
            input: false,
            scroll: false,
            submit: false,
            capture_text_content: false,
          },
        });

        type ChameleonSourcePromises = {
          [key: string]: Promise<EventTarget>;
        };
        type ChameleonSinkPromises = {
          adobe: Promise<void>;
          gtm: typeof gtmSink;
          mixpanel: typeof mixpanelSink;
        };

        const initialisedAnalytics = initialise({
          sources: {
            fakeSource,
          } as ChameleonSourcePromises,
          sinks: {
            gtm: (payload: { events?: string[]; data: unknown }) =>
              gtmSink({
                ...payload,
                events: payload.events?.filter(shouldSendGtmEvent),
              }),
            adobe: waitingSink,
            mixpanel: (events: Definition[]) =>
              mixpanelSink(
                events.filter(({ event }) => shouldSendMixpanelEvent(event)),
              ),
          } as ChameleonSinkPromises,
          config: { logError: logger },
        });

        handleOneTrustLoad(initialisedAnalytics.pushToDataLayer);
        resolve({
          pushToDataLayer: initialisedAnalytics.pushToDataLayer,
          resetDataLayer: initialisedAnalytics.resetDataLayer,
          setSuperProperties: bridgeSetSuperProperties,
        });
        setIsReady(true);
      },
    );
  }, [isReady, fakeSource, waitingSink, resolve]);

  const pushToDataLayer = useCallback(
    (...args: Parameters<PushToDataLayer>) =>
      promise.then(async ({ pushToDataLayer }) => {
        const [definitions] = args;
        if (
          !definitions.pageData &&
          (definitions.firstContentfulPaintData ||
            definitions.timeToFirstByteData)
        ) {
          await firstPageDataSent;
        }

        const result = await pushToDataLayer(...args);
        if (definitions.pageData) {
          resolveFirstPageDataSent();
        }
        return result;
      }),
    [promise],
  );

  const resetDataLayer = useCallback(
    (pathname?: string | null) => {
      // Close the vitals window before any asynchronous page identity updates.
      if (pathname != null) {
        initialPathname ??= pathname;
        hasNavigated ||= pathname !== initialPathname;
      }
      return promise.then(({ resetDataLayer }) => resetDataLayer());
    },
    [promise],
  );

  const setSuperProperties = useCallback(
    (properties: Record<string, unknown>) =>
      promise.then(({ setSuperProperties }) => setSuperProperties(properties)),
    [promise],
  );

  return (
    <>
      <AnalyticsContext.Provider
        value={{ pushToDataLayer, resetDataLayer, setSuperProperties }}
      >
        {children}
      </AnalyticsContext.Provider>
      <Script
        id="adobe-web-sdk"
        src={ADOBE_ANALYTICS_SCRIPT_URL}
        type="text/javascript"
        async
        onLoad={resolveWaitingSink}
      ></Script>
    </>
  );
};
